import {
  onBeforeUnmount,
  ref,
  nextTick,
  watch,
  onMounted,
} from '@nuxtjs/composition-api'

export type HeaderCalculation = {
  totalHeight: number
  collapsedHeight: number
  headerHeight: number
  categoriesHeight: number
  searchInputHeight: number
  remValue: number
}

const initHeaderCalculation = () => ({
  totalHeight: 0,
  collapsedHeight: 0,
  headerHeight: 0,
  categoriesHeight: 0,
  searchInputHeight: 0,
  remValue: 0,
})

export function useSearchBotHeaderLayout(
  isMobile: any,
  headerButtonsElement: Ref<UnwrapRef<HTMLElement>>,
  headerTopMessageElement: Ref<UnwrapRef<HTMLElement>>,
  active: any
) {
  const searchBotElement = ref<HTMLElement>(null)
  const headerWrapperElement = ref<HTMLElement>(null)
  const headerStretcherElement = ref<HTMLElement>(null)
  const searchBotCollapsed = ref<boolean>(false)
  const searchBotMounted = ref<boolean>(false)

  const appear = ref<boolean>(false)
  const fullsize = ref<boolean>(false)

  const opacityValue = ref(1)
  const scaleValue = ref(1)

  const headerCalc = initHeaderCalculation()

  let animateCallback = null
  let resumeAnimationCallback = null
  let measureCallback = null

  const measureHeaderElements = () => {
    try {
      const htmlElement = document.documentElement
      const computedStyle = window.getComputedStyle(htmlElement)

      headerCalc.remValue = parseFloat(
        computedStyle.getPropertyValue('font-size')
      )

      headerCalc.totalHeight = 0
      headerCalc.collapsedHeight = 0

      const headerEl = document.querySelector<HTMLElement>('.header')
      if (headerEl) {
        headerCalc.headerHeight = headerEl.getBoundingClientRect().height
        headerCalc.totalHeight += headerCalc.headerHeight
        headerCalc.collapsedHeight += headerCalc.headerHeight
      }

      const searchInputEl = document.querySelector<HTMLElement>(
        '.section-chatbot__input'
      )
      if (searchInputEl) {
        headerCalc.searchInputHeight =
          searchInputEl.getBoundingClientRect().height
        headerCalc.totalHeight += headerCalc.searchInputHeight
      }

      const categoriesEl = document.querySelector<HTMLElement>(
        '.header-categories-block'
      )
      if (categoriesEl) {
        headerCalc.categoriesHeight =
          categoriesEl.getBoundingClientRect().height
        headerCalc.totalHeight += headerCalc.categoriesHeight
        headerCalc.collapsedHeight += headerCalc.categoriesHeight
      }

      // Небольшой запас на случай если рамка инпута обрезается
      headerCalc.totalHeight = Math.ceil(headerCalc.totalHeight) + 1
      headerCalc.collapsedHeight = Math.ceil(headerCalc.collapsedHeight) + 1
    } catch (e) {
      console.log(e)
    }
  }

  const adjustHeaderLayout = () => {
    const containerEl = document.querySelector<HTMLElement>(
      '.section-chatbot__container'
    )
    if (containerEl) {
      containerEl.style.paddingTop = `${headerCalc.searchInputHeight}px`
    }
  }

  const measureHeaderAnimation = () => {
    try {
      const header = headerWrapperElement.value
      const scroll = window.scrollY
      let totalTop = 0
      totalTop += headerButtonsElement.value?.offsetHeight || 0
      totalTop += headerTopMessageElement.value?.offsetHeight || 0
      const top = totalTop || headerCalc.remValue * 2.4

      header.style.minHeight = `${headerCalc.collapsedHeight}px`

      header.style.top = `${Math.floor(Math.max(0, top - scroll))}px`

      const maxHeight = headerCalc.totalHeight

      const subtractScroll = scroll >= top ? scroll - top : 0

      header.style.height = `${Math.floor(
        Math.max(0, maxHeight - subtractScroll)
      )}px`

      const searchInputOverlap = Math.min(
        1,
        Math.max(0, (scroll - top) / headerCalc.headerHeight)
      )

      opacityValue.value = Math.max(0, 1 - 1.4 * searchInputOverlap)
      scaleValue.value = Math.max(0, 1 - 0.1 * searchInputOverlap)

      searchBotCollapsed.value =
        header.offsetHeight <= headerCalc.collapsedHeight
    } catch (e) {
      console.log(e)
    }
  }

  const animate = () => {
    measureHeaderAnimation()
    headerWrapperElement.value.style.transition = 'none'
    animateCallback = requestAnimationFrame(animate)
  }

  const onToggleSearchBot = (value: boolean) => {
    const surveyButton = document.querySelector<HTMLElement>('.survey-button')
    const header = headerWrapperElement.value
    if (value) {
      cancelAnimationFrame(animateCallback)
      clearTimeout(resumeAnimationCallback)
      header.style.top = '0'
      header.style.height = `100dvh`
      header.style.transition = 'height, top, 0.8s ease-out'
      document.body.style.overflow = 'hidden'
      surveyButton?.style.display = 'none'
      header.scrollTop = header.scrollHeight
    } else {
      measureHeaderAnimation()
      resumeAnimationCallback = setTimeout(animate, 800)
      document.body.style.overflow = ''
      surveyButton?.style.display = ''
    }
    fullsize.value = value
  }

  const onToggleAppear = (value: boolean) => {
    measureHeaderElements()
    const header = headerWrapperElement.value
    const stretcher = headerStretcherElement.value
    if (value) {
      stretcher.style.height = `calc(1.6rem + ${headerCalc.totalHeight}px)`
      if (!isMobile.value) {
        measureHeaderAnimation()
        header.style.transition = 'height, top, 0.8s ease-out'
        resumeAnimationCallback = setTimeout(() => {
          measureHeaderAnimation()
          animate()
        }, 800)
      }
    } else {
      stretcher.style.height = ''
      if (!isMobile.value) {
        header.style.height = ''
        header.style.transition = 'height, top, 0.8s ease-out'
        cancelAnimationFrame(animateCallback)
        clearTimeout(resumeAnimationCallback)
      }
    }
    appear.value = value
  }

  watch(
    [() => headerWrapperElement.value, () => searchBotMounted.value],
    () => {
      if (!process.browser) return
      if (
        !active.value ||
        !headerWrapperElement.value ||
        !searchBotMounted.value
      ) {
        clearInterval(measureCallback)
        cancelAnimationFrame(animateCallback)
        clearTimeout(resumeAnimationCallback)
        return
      }
      measureHeaderElements()
      adjustHeaderLayout()

      measureCallback = setInterval(() => {
        measureHeaderElements()
        adjustHeaderLayout()
      }, 200)

      if (active.value && isMobile.value) {
        animate()
      }
      nextTick(() => {
        onToggleAppear(true)
      })
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    clearInterval(measureCallback)
    cancelAnimationFrame(animateCallback)
    clearTimeout(resumeAnimationCallback)
    searchBotMounted.value = false
  })

  const onCloseSearchBot = () => {
    onToggleSearchBot(false)
  }

  const onSearchBotMounted = () => {
    searchBotMounted.value = true
  }

  // onMounted(() => {
  //   document.body.addEventListener('keydown', (e) => {
  //     if (e.code === 'KeyP') {
  //       onToggleAppear(!appear.value)
  //     }
  //     if (e.code === 'KeyO') {
  //       onToggleSearchBot(!fullsize.value)
  //     }
  //   })
  // })
  return {
    searchBotElement,
    headerWrapperElement,
    headerStretcherElement,
    searchBotCollapsed,
    appear,
    fullsize,
    onToggleSearchBot,
    onCloseSearchBot,
    opacityValue,
    scaleValue,
    onSearchBotMounted,
  }
}
